import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Share2, Trash2, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import ShareAnalysisDialog from './ShareAnalysisDialog';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

const AnalysisDetail = ({ analysis, onBack, onDelete, onAnalysisUpdate, isDeleting = false, onRefreshAnalyses, onUpdateAnalysisInList }) => {
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formattedAnalysis, setFormattedAnalysis] = useState(null);
  const [currentAnalysis, setCurrentAnalysis] = useState(analysis);
  const { currentUser, getIdToken } = useAuth();

  // Update local state when prop changes
  useEffect(() => {
    setCurrentAnalysis(analysis);
  }, [analysis]);

  // Poll for analysis updates when processing
  useEffect(() => {
    if (!currentAnalysis || !currentAnalysis.id) return;

    // If already processed, no need to poll
    if (currentAnalysis.processed) return;

    // Poll every 3 seconds
    const interval = setInterval(async () => {
      try {
        const token = await getIdToken();
        const response = await fetch(`${API_BASE_URL}/analyses/${currentAnalysis.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const updatedAnalysis = await response.json();

          // If status changed to processed, update and stop polling
          if (updatedAnalysis.processed !== currentAnalysis.processed) {
            setCurrentAnalysis(updatedAnalysis);
            // Notify parent component
            if (onAnalysisUpdate) {
              onAnalysisUpdate(updatedAnalysis);
            }
            // Fetch formatted analysis for the updated data
            setFormattedAnalysis(updatedAnalysis.formatted_html || updatedAnalysis.analysis_text || '');
            clearInterval(interval);
          }
        }
      } catch (err) {
        console.error('Error polling analysis status:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [currentAnalysis, getIdToken, onAnalysisUpdate]);

  // Fetch formatted analysis on component mount
  useEffect(() => {
    if (currentAnalysis && currentAnalysis.id) {
      fetchFormattedAnalysis();
    }
  }, [currentAnalysis]);

  const fetchFormattedAnalysis = async () => {
    try {
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/analyses/${currentAnalysis.id}/formatted`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch formatted analysis: ${response.statusText}`);
      }
      
      const data = await response.json();
      setFormattedAnalysis(data.html || currentAnalysis.text);
    } catch (err) {
      console.error('Error fetching formatted analysis:', err);
      // Fallback to the original analysis text
      setFormattedAnalysis(currentAnalysis.analysis || currentAnalysis.text || '');
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(8, 10);
      const minute = timestamp.substring(10, 12);
      return `${year}/${month}/${day} ${hour}:${minute}`;
    } catch (e) {
      return timestamp;
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');
      
      // Create a loading indicator
      const loadingIndicator = document.createElement('div');
      loadingIndicator.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      loadingIndicator.innerHTML = `
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p class="text-center text-lg font-medium">Generating PDF...</p>
          <p class="text-center text-sm text-gray-500">This may take a few moments</p>
        </div>
      `;
      document.body.appendChild(loadingIndicator);
      
      // Request the PDF from the server with appropriate authentication
      console.log("ID:",currentAnalysis.id)
      const response = await fetch(`${API_BASE_URL}/analyses/${currentAnalysis.id}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to generate PDF: ${response.statusText}`);
      }
      
      // Get the PDF as a blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link to download the PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = `dance_analysis_${currentAnalysis.dance_type || 'report'}.pdf`;
      
      // Append link to body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(loadingIndicator);
      setLoading(false);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(`Failed to download PDF: ${err.message}`);
      
      // Remove loading indicator if there was an error
      const loadingIndicator = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
      if (loadingIndicator) {
        document.body.removeChild(loadingIndicator);
      }
      setLoading(false);
    }
  };
  
  const handleRerunAnalysis = async () => {
    if (!window.confirm('Are you sure you want to rerun this analysis? This may take several minutes.')) {
      return;
    }

    console.log('=== START RERUN ANALYSIS ===');
    console.log('Current analysis:', currentAnalysis);

    try {
      setLoading(true);
      setError(null);

      const token = await getIdToken();
      console.log('Got token:', !!token);

      const response = await fetch(`${API_BASE_URL}/analyses/${currentAnalysis.id}/rerun`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Rerun API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Rerun API error:', errorData);
        throw new Error(errorData.error || 'Failed to rerun analysis');
      }

      // Get the response data - backend might return updated analysis
      const responseText = await response.text();
      console.log('Rerun API raw response:', responseText);

      let updatedAnalysis = null;
      try {
        updatedAnalysis = JSON.parse(responseText);
      } catch (e) {
        console.log('Response is not JSON, using empty object');
        updatedAnalysis = {};
      }

      console.log('Rerun parsed response:', updatedAnalysis);

      // Update the local analysis to show processing status
      const processingUpdates = {
        processed: false,
        status: 'processing'
      };

      // If backend returned updated data, use that; otherwise use our manually set processing status
      if (updatedAnalysis && typeof updatedAnalysis === 'object') {
        processingUpdates.processed = updatedAnalysis.processed !== undefined ? updatedAnalysis.processed : false;
        processingUpdates.status = updatedAnalysis.status || 'processing';
      }

      console.log('Processing updates to apply:', processingUpdates);
      console.log('Analysis ID to update:', currentAnalysis.id);
      console.log('onUpdateAnalysisInList function exists:', !!onUpdateAnalysisInList);
      console.log('onAnalysisUpdate function exists:', !!onAnalysisUpdate);

      // Immediately update the analysis in the parent's list
      if (onUpdateAnalysisInList) {
        console.log('Calling onUpdateAnalysisInList...');
        onUpdateAnalysisInList(currentAnalysis.id, processingUpdates);
        console.log('onUpdateAnalysisInList called');
      } else {
        console.error('onUpdateAnalysisInList is NOT defined!');
      }

      // Also update the current analysis state
      if (onAnalysisUpdate) {
        console.log('Calling onAnalysisUpdate...');
        onAnalysisUpdate({ ...currentAnalysis, ...processingUpdates });
        console.log('onAnalysisUpdate called');
      }

      // Wait a moment for state to propagate
      await new Promise(resolve => setTimeout(resolve, 500));

      // Show success message
      alert('Analysis rerun started successfully. This may take several minutes to complete.');

      console.log('About to call onBack...');

      if (onBack) {
        console.log('Calling onBack()');
        // Go back to the main screen after initiating rerun
        onBack();
      }
    } catch (err) {
      console.error('Error rerunning analysis:', err);
      setError(`Failed to rerun analysis: ${err.message}`);
    } finally {
      setLoading(false);
      console.log('=== END RERUN ANALYSIS ===');
    }
  };

  // Check if the current user is the owner of this analysis
  const isOwner = currentUser && currentAnalysis.user_id === currentUser.uid;
  
  // Check if analysis is in progress/unprocessed
  const isProcessing = !currentAnalysis.processed;

  return (
    <div className="h-full relative">
      {isDeleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <Loader2 className="w-10 h-10 text-red-600 animate-spin mx-auto mb-4" />
            <p className="text-center text-lg font-medium">Deleting analysis...</p>
            <p className="text-center text-sm text-gray-500">Please wait</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors min-h-[44px] px-2"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span className="text-sm sm:text-base">Back to Analyses</span>
        </button>

        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {/* Only show share button if user is the owner */}
          {isOwner && (
            <button
              onClick={() => setShowShareDialog(true)}
              className="flex items-center justify-center px-4 py-2.5 text-sm border rounded-lg hover:bg-gray-50 min-h-[44px] flex-1 sm:flex-none"
              title="Share with others"
              disabled={loading}
            >
              <Share2 className="w-5 h-5 mr-1" />
              <span className="hidden sm:inline">Share</span>
            </button>
          )}

          <button
            onClick={handleDownloadPDF}
            className="flex items-center justify-center px-4 py-2.5 text-sm border rounded-lg hover:bg-gray-50 min-h-[44px] flex-1 sm:flex-none"
            title="Download PDF"
            disabled={loading}
          >
            <Download className="w-5 h-5 mr-1" />
            <span className="hidden sm:inline">PDF</span>
          </button>

          {/* Show rerun button if analysis is in progress or the user is the owner */}
          {(isProcessing || isOwner) && (
            <button
              onClick={handleRerunAnalysis}
              className="flex items-center justify-center px-4 py-2.5 text-sm border rounded-lg hover:bg-gray-50 min-h-[44px] flex-1 sm:flex-none"
              title="Rerun Analysis"
              disabled={loading}
            >
              <RefreshCw className="w-5 h-5 mr-1" />
              <span className="hidden sm:inline">Rerun</span>
            </button>
          )}

          {/* Only show delete button if user is the owner */}
          {isOwner && onDelete && (
            <button
              onClick={() => onDelete(currentAnalysis.id)}
              className="flex items-center justify-center px-4 py-2.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex-1 sm:flex-none"
              title="Delete analysis"
              disabled={loading || isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="w-5 h-5 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5 mr-1" />
              )}
              <span className="hidden sm:inline">{isDeleting ? 'Deleting...' : 'Delete'}</span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 text-gray-800 p-4 sm:p-6">
          <h1 className="text-xl sm:text-2xl font-bold mb-2">Dance Analysis Report</h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-sm mt-4">
            <div>
              <p className="opacity-75 mb-1">Date:</p>
              <p className="font-medium">{formatDate(currentAnalysis.timestamp)}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Dance Type:</p>
              <p className="font-medium">{currentAnalysis.dance_type}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Dancers:</p>
              <p className="font-medium">{currentAnalysis.dancers}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Analysis Type:</p>
              <p className="font-medium capitalize">
                {currentAnalysis.analysis_type === 'solo' 
                  ? 'Solo Analysis' 
                  : currentAnalysis.analysis_type === 'duo'
                    ? 'Duo Analysis'
                    : currentAnalysis.analysis_type === 'formation'
                      ? 'Formation Analysis'
                      : 'Couple Analysis'}
              </p>
            </div>
            
            {/* Add Total Score display */}
            {currentAnalysis.total_score && (
              <div>
                <p className="opacity-75 mb-1">Total Score:</p>
                <p className="font-medium text-blue-600 text-xl">
                  {currentAnalysis.total_score}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Video Section */}
        {currentAnalysis.video_url && (
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">Performance Video</h2>
            <div className="aspect-video">
              <VideoPlayer 
                videoUrl={currentAnalysis.video_url} 
                className="w-full h-full rounded-lg shadow"
              />
            </div>
          </div>
        )}

        {/* Analysis Content */}
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Analysis</h2>
          
          {isProcessing ? (
            <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-lg text-gray-600 mb-2">Analysis in progress...</p>
              <p className="text-sm text-gray-500">This may take several minutes to complete.</p>
            </div>
          ) : (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{
                __html: formattedAnalysis || currentAnalysis.analysis || currentAnalysis.text || 'No analysis content available.',
              }}
            />
          )}
        </div>
      </div>
      
      {/* Share Dialog */}
      {showShareDialog && (
        <ShareAnalysisDialog 
          analysisId={currentAnalysis.id}
          onClose={() => setShowShareDialog(false)}
          onSuccess={() => {
            setShowShareDialog(false);
            alert('Analysis shared successfully!');
          }}
        />
      )}
    </div>
  );
};

export default AnalysisDetail;